import { EmsConfiguration, EmsConfigurationDto, LicenseDelivery, toEmsConfigurationDto } from "domain/licenses";
import {
    CombinedTier,
    CommonTenant,
    ContractType,
    FetchRegionsListResponse,
    LicensingModel,
    LoginMethod,
    Tenant,
    TenantContractStatus,
    TenantCursor,
    TenantsData,
    TenantType,
} from "domain/tenants";
import { FeatureLicenseType, RoleDto, toFeatureLicenseType, toRole } from "domain/users";
import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";
import { LicenseDeliveryDto, toLicenseDelivery } from "services/licenses/LicenseService";
import { ParameterQuery, trimToUndefined } from "utils/commonFunctions";

export interface TenantLicense {
    identifier: string;
    assigned: number;
    used: number;
    type: string;
    expiration: string;
}

export interface LicenseTokenRates {
    name: string;
    uuid: string;
    product_to_rate: [
        {
            productId: string;
            rate: number;
        },
    ];
}

interface TenantDto extends CommonTenant {
    deletion_status: boolean;
    contact_email: string;
    ems_id: string;
    contact_name: string;
    country_code: string;
    salesforce_lead_id: string;
    created_by: string;
    created_timestamp: string;
    erasure_client_endpoint: string;
    public_api_endpoint: string;
    usage_statistics: boolean;
    roles?: RoleDto[];
    expiration_date: string;
    region: string;
    status: boolean;
    tier: CombinedTier;
    salesforce_id: string;
    type: TenantType;
    total_reports_count?: number;
    total_users?: number;
    total_workflows?: number;
    overall_total_reports_count?: number;
    overall_total_users?: number;
    overall_total_workflows?: number;
    products_licensed: [];
    total_available_licenses?: number;
    parent_expiration_date: string;
    login_method?: LoginMethod;
    level: number;
    tenants?: TenantDto[];
    parent_uuid: string;
    found_user?: string;
    found_user_name?: string;
    sub_tenants_count: number;
    overall_total_subtenants?: number;
    total_logins?: number;
    overall_total_logins?: number;
    last_logged_in_user?: string;
    last_login?: string;
    licensing_model: LicensingModel;
    rate_uuid?: string;
    token_rate?: LicenseTokenRates;
    ip_restrictions: [];
    parent_licensing_model: LicensingModel;
    parent_type: TenantType;
    parent_tier: CombinedTier;
    salesforce_contract_id?: string;
    salesforce_contract_state?: TenantContractStatus;
    salesforce_contact_email?: string;
    salesforce_contract_end_date?: string;
    parent_drawback_prevention: boolean;
    drawback_prevention?: boolean;
    common_licenses?: number;
    contract_type: ContractType;
    parent_contract_type: ContractType;
    automatic_deletion?: boolean;
    refresh_token_expiration?: number;
    news_email_opt_in?: boolean;
    disk_quota_bytes?: number;
    upload_limit_bytes?: number;
    download_limit_bytes?: number;
}

export interface TenantsDto {
    tenants: TenantDto[];
    cursor: string;
    name_is_available?: boolean;
}

interface AccessTenantResponseDto {
    message: string;
    stan: string;
    laurel: string;
    arthur: string;
    jefferson: string;
    oliver: string;
    publicApi: string;
    publicApiDocumentation: string;
    feature_licenses: string[];
    region?: string;
    tenant_type: TenantType;
    tenant_name: string;
    login_method?: LoginMethod;
    licensing_model: LicensingModel;
    tenant_tier: string;
    parent_drawback_prevention: boolean;
    contract_type: ContractType;
    expiration_date: string;
}

interface AccessTenantResponse {
    message: string;
    stan: string;
    laurel: string;
    arthur: string;
    jefferson: string;
    oliver: string;
    publicApi: string;
    publicApiDocumentation: string;
    featureLicenses: FeatureLicenseType[];
    region?: string;
    type: TenantType;
    tenantName: string;
    loginMethod?: LoginMethod;
    licensingModel: LicensingModel;
    tenantTier: string;
    parentDrawbackPrevention: boolean;
    contractType: ContractType;
    expirationDate?: string;
}

interface TargetParentDto {
    uuid: string;
    name: string;
    parent_uuid: string;
    tier: CombinedTier;
    type: TenantType;
    licensing_model: LicensingModel;
    combined_depth_exceeded: boolean;
    parent_level: number;
}

interface TargetTenantResponseDto {
    target_parents: TargetParentDto[];
}

interface TargetTenantResponse {
    uuid: string;
    name: string;
    parentUuid: string;
    tier: string;
    combinedDepthExceeded: boolean;
    parentLevel: number;
    licensingModel: LicensingModel;
    type: TenantType;
}

export interface EditTenantDto {
    tenantStatus: boolean | null;
    emsConfiguration: EmsConfiguration;
    expirationDate: string;
    customerName?: string | null;
    countryCode?: string | null;
    contactName?: string | null;
    contactEmail?: string | null;
    notes?: string | null;
    salesforceId?: string | null;
    tier: CombinedTier;
    licensingModel: LicensingModel;
    licenseDelivery: LicenseDelivery | null;
    type: string;
    contractType: ContractType;
    salesforceLeadId?: string;
    salesforceContractId?: string;
    salesforceContractState?: TenantContractStatus | string;
    salesforceContactEmail?: string;
    salesforceContractEndDate?: string;
    drawbackPrevention?: boolean;
    automaticDeletion?: boolean;
    diskQuotaMib?: number;
    uploadLimitMib?: number;
    downloadLimitMib?: number;
}

export interface EditTenantRequest {
    name?: string;
    status?: string | boolean;
    ems: EmsConfigurationDto;
    expiration_date: string;
    customer_name?: string;
    country_code?: string;
    contact_name?: string;
    contact_email?: string;
    salesforce_id?: string;
    notes?: string;
    tier: CombinedTier;
    licensing_model?: LicensingModel;
    license_delivery?: LicenseDeliveryDto | null;
    type?: string;
    contract_type: string;
    salesforce_lead_id?: string;
    salesforce_contract_id?: string;
    salesforce_contract_state?: TenantContractStatus | string;
    salesforce_contact_email?: string;
    salesforce_contract_end_date?: string;
    drawback_prevention?: boolean;
    automatic_deletion?: boolean;
    disk_quota_bytes?: number;
    upload_limit_bytes?: number;
    download_limit_bytes?: number;
}

export interface CreateTenantResponse {
    message: string;
    warnings: string[] | undefined;
}

export interface TenantHierarchyResponse {
    uuid: string;
    name: string;
    tenants: TenantHierarchyResponse[];
}

export interface TenantDeletionNotificationDataDto {
    tenant_name: string;
}

export interface TenantDeletionNotificationData {
    tenantName: string;
}

function toNumber(value?: number) {
    return value ? value : 0;
}

const BYTES_PER_MIB = 1_024 * 1_024;

function toTenant(dto: TenantDto): Tenant {
    return {
        name: dto.name,
        notes: dto.notes,
        region: dto.region,
        uuid: dto.uuid,
        countryCode: dto.country_code,
        contactName: dto.contact_name,
        contactEmail: dto.contact_email,
        salesforceLeadId: dto.salesforce_lead_id,
        emsId: dto.ems_id,
        createdTimestamp: dto.created_timestamp,
        createdBy: dto.created_by,
        erasureClientEndpoint: dto.erasure_client_endpoint,
        publicApiEndpoint: dto.public_api_endpoint,
        usageStatistics: dto.usage_statistics,
        status: dto.status,
        roles: (dto.roles || []).map(toRole),
        expirationDate: dto.expiration_date,
        tier: dto.tier,
        salesforceAccountId: dto.salesforce_id,
        type: dto.type,
        totalReportsCount: toNumber(dto.total_reports_count),
        totalUsers: toNumber(dto.total_users),
        totalWorkflows: toNumber(dto.total_workflows),
        overallTotalReportsCount: toNumber(dto.overall_total_reports_count),
        overallTotalUsers: toNumber(dto.overall_total_users),
        overallTotalWorkflows: toNumber(dto.overall_total_workflows),
        overallSubTenantsCount: toNumber(dto.overall_total_subtenants),
        totalLogins: toNumber(dto.total_logins),
        overallTotalLogins: toNumber(dto.overall_total_logins),
        lastLoggedInUser: dto.last_logged_in_user,
        lastLogin: dto.last_login,
        productsLicensed: dto.products_licensed,
        availableLicenses: toNumber(dto.total_available_licenses),
        parentExpirationDate: dto.parent_expiration_date,
        loginMethod: dto.login_method,
        deletionStatus: dto.deletion_status,
        tenantHierarchyLevel: dto.level,
        tenants: (dto.tenants || []).map(toTenant),
        parentUuid: dto.parent_uuid,
        foundUser: dto.found_user,
        foundUserName: dto.found_user_name,
        subTenantsCount: dto.sub_tenants_count,
        licensingModel: dto.licensing_model,
        rateUuid: dto.rate_uuid,
        ipRestrictions: dto.ip_restrictions,
        parentLicensingModel: dto.parent_licensing_model,
        parentType: dto.parent_type,
        parentTier: dto.parent_tier,
        salesforceContractId: dto.salesforce_contract_id || "",
        salesforceContractState: dto.salesforce_contract_state,
        salesforceContactEmail: dto.salesforce_contact_email,
        salesforceContractEndDate: dto.salesforce_contract_end_date,
        commonLicenseCount: dto.common_licenses,
        parentDrawbackPrevention: dto.parent_drawback_prevention,
        drawbackPrevention: dto.drawback_prevention,
        contractType: dto.contract_type,
        parentContractType: dto.parent_contract_type,
        automaticDeletion: dto.automatic_deletion,
        refreshTokenExpiration: dto.refresh_token_expiration,
        newsEmailOptIn: dto.news_email_opt_in,
        diskQuotaMib: dto.disk_quota_bytes != null ? dto.disk_quota_bytes / BYTES_PER_MIB : undefined,
        uploadLimitMib: dto.upload_limit_bytes != null ? dto.upload_limit_bytes / BYTES_PER_MIB : undefined,
        downloadLimitMib: dto.download_limit_bytes != null ? dto.download_limit_bytes / BYTES_PER_MIB : undefined,
    };
}

function toKeyCursor(cursor: string | null): TenantCursor | null {
    return cursor == null ? null : JSON.parse(cursor);
}

const PATH_TENANT = "/api/tenants";
const PATH_V1_TENANT = "/api/v1/tenants";
// Client-side timeout set below API Gateway's 29s limit. When API Gateway times out, fetch throws
// a generic exception with no way to identify the cause, so we timeout first to detect it reliably.
const TENANT_SETTINGS_UPDATE_TIMEOUT = 28995;

function toAccessTenantResponse(dto: AccessTenantResponseDto): AccessTenantResponse {
    const featureLicenses: FeatureLicenseType[] = dto.feature_licenses.map((l) => toFeatureLicenseType(l));
    return {
        message: dto.message,
        stan: dto.stan,
        laurel: dto.laurel,
        arthur: dto.arthur,
        jefferson: dto.jefferson,
        oliver: dto.oliver,
        publicApi: dto.publicApi,
        publicApiDocumentation: dto.publicApiDocumentation,
        featureLicenses: featureLicenses,
        region: dto.region,
        type: dto.tenant_type,
        tenantName: dto.tenant_name,
        loginMethod: dto.login_method,
        licensingModel: dto.licensing_model,
        tenantTier: dto.tenant_tier,
        parentDrawbackPrevention: dto.parent_drawback_prevention,
        contractType: dto.contract_type,
        expirationDate: dto.expiration_date,
    };
}

function toTargetParentResponse(dto: TargetTenantResponseDto): TargetTenantResponse[] {
    return dto.target_parents.map((tenant) => ({
        uuid: tenant.uuid,
        name: tenant.name,
        parentUuid: tenant.parent_uuid,
        tier: tenant.tier,
        combinedDepthExceeded: tenant.combined_depth_exceeded,
        parentLevel: tenant.parent_level,
        licensingModel: tenant.licensing_model,
        type: tenant.type,
    }));
}

export class TenantService {
    public fetchTenants(
        abortController: AbortController,
        cursor: TenantCursor | null,
        search?: string,
        username?: string,
        all?: string,
        tenantName?: string,
        usecaseType?: string
    ): Promise<TenantsData> {
        const apiUrl =
            PATH_TENANT +
            new ParameterQuery()
                .add("search", search)
                .add("username", username)
                .add("all", all)
                .add("cursor", cursor == null ? "" : JSON.stringify(cursor))
                .add("tenant_name", tenantName)
                .add("type", usecaseType)
                .createQueryString();
        return apiGatewayService
            .invokeApi(apiUrl, "GET", null, { abortSignal: abortController.signal, apiType: ApiType.LAUREL })
            .then((response: TenantsDto) => {
                if (response["name_is_available"] !== undefined) {
                    return {
                        message: response.name_is_available,
                        tenantList: {
                            tenantList: [],
                            cursor: null,
                        },
                    };
                } else {
                    const tenantList = response.tenants.map(toTenant);
                    const cursor = toKeyCursor(response.cursor);
                    return {
                        tenantList: {
                            tenantList: tenantList,
                            cursor: cursor,
                        },
                    };
                }
            });
    }

    public createTenant(
        name: string,
        contract_type: string,
        type: string,
        region: string,
        tier: CombinedTier,
        countryCode: string,
        notes: string,
        contactName: string,
        contactEmail: string,
        expirationDate: string,
        salesforceAccountId: string,
        ems: EmsConfiguration,
        parentUuid: string | undefined,
        licenseDelivery: LicenseDelivery,
        licensingModel: LicensingModel,
        abortController: AbortController,
        salesforceContractId?: string,
        salesforceContractState?: TenantContractStatus | string,
        salesforceContactEmail?: string,
        salesforceContractEndDate?: string,
        salesForceLeadId?: string,
        drawbackPrevention?: boolean,
        automaticDeletion?: boolean
    ): Promise<CreateTenantResponse> {
        return apiGatewayService.invokeApi(
            PATH_TENANT,
            "POST",
            {
                name,
                contract_type,
                type,
                region,
                tier,
                country_code: countryCode,
                notes,
                contact_name: contactName,
                contact_email: contactEmail,
                salesforce_id: salesforceAccountId,
                ems: toEmsConfigurationDto(ems),
                license_delivery: toLicenseDelivery(licenseDelivery),
                expiration_date: expirationDate,
                parent_uuid: parentUuid,
                licensing_model: licensingModel,
                salesforce_contract_id: salesforceContractId ?? salesforceContractId,
                salesforce_contract_state: salesforceContractState ?? salesforceContractState,
                salesforce_contact_email: salesforceContactEmail ?? salesforceContactEmail,
                salesforce_contract_end_date: salesforceContractEndDate ?? null,
                salesforce_lead_id: salesForceLeadId ?? "",
                drawback_prevention: drawbackPrevention,
                automatic_deletion: automaticDeletion,
            },
            { abortSignal: abortController.signal, apiType: ApiType.LAUREL }
        );
    }

    public accessTenant(uuid: string, abortController: AbortController): Promise<AccessTenantResponse> {
        return apiGatewayService
            .invokeApi(PATH_TENANT + "/" + uuid + "/access", "GET", null, {
                abortSignal: abortController.signal,
                apiType: ApiType.LAUREL,
            })
            .then((dto: AccessTenantResponseDto) => toAccessTenantResponse(dto));
    }

    public fetchMovableTenants(uuid: string, abortController: AbortController): Promise<TargetTenantResponse[]> {
        return apiGatewayService
            .invokeApi(PATH_V1_TENANT + "/" + uuid + "/target-parents", "GET", null, {
                abortSignal: abortController.signal,
                apiType: ApiType.ARTHUR,
            })
            .then((dto: TargetTenantResponseDto) => toTargetParentResponse(dto));
    }

    public moveTenant(tenantUuid: string, parentUuid: string, abortController?: AbortController): Promise<void> {
        return apiGatewayService.invokeApi(
            PATH_V1_TENANT + "/" + tenantUuid + "/move?new_parent_uuid=" + parentUuid,
            "POST",
            null,
            {
                abortSignal: abortController?.signal,
                apiType: ApiType.ARTHUR,
                emptyResponse: true,
            }
        );
    }

    public fetchTenant(uuid: string, abortController?: AbortController): Promise<Tenant> {
        return apiGatewayService
            .invokeApi(PATH_TENANT + "/" + uuid, "GET", null, {
                abortSignal: abortController?.signal,
                apiType: ApiType.LAUREL,
            })
            .then((dtos: TenantDto) => toTenant(dtos));
    }

    public fetchRegionsList(): Promise<FetchRegionsListResponse> {
        return apiGatewayService.invokeApi("/regions", "GET", null);
    }

    public updateTenantSettings(
        uuid: string,
        statisticsEnabled: boolean | undefined,
        loginMethod: LoginMethod | undefined,
        ipRestrictions: string[] | undefined,
        sessionDuration: number | undefined,
        sharedSettingsNewsEmailAlert: boolean | undefined,
        abortController: AbortController
    ): Promise<void> {
        const api_url = PATH_V1_TENANT + "/" + uuid + "/settings";
        return apiGatewayService.invokeApi(
            api_url,
            "POST",
            {
                statistics_enabled: statisticsEnabled == null ? undefined : statisticsEnabled,
                login_method: trimToUndefined(loginMethod),
                ip_restrictions: ipRestrictions,
                refresh_token_expiration: sessionDuration,
                news_email_opt_in: sharedSettingsNewsEmailAlert,
            },
            {
                abortSignal: abortController.signal,
                apiType: ApiType.ARTHUR,
                emptyResponse: true,
                timeout: TENANT_SETTINGS_UPDATE_TIMEOUT,
            }
        );
    }

    public editTenant(
        uuid: string,
        editTenant: EditTenantDto,
        abortController: AbortController
    ): Promise<CreateTenantResponse> {
        const postData: EditTenantRequest = {
            tier: editTenant.tier,
            ems: toEmsConfigurationDto(editTenant.emsConfiguration),
            expiration_date: editTenant.expirationDate,
            license_delivery: toLicenseDelivery(editTenant.licenseDelivery),
            contract_type: editTenant.contractType,
            automatic_deletion: editTenant.automaticDeletion,
        };

        if (editTenant.tenantStatus != null) {
            postData.status = editTenant.tenantStatus;
        }
        if (editTenant.customerName != null) {
            postData.name = editTenant.customerName;
        }
        if (editTenant.countryCode != null) {
            postData.country_code = editTenant.countryCode;
        }
        if (editTenant.contactName != null) {
            postData.contact_name = editTenant.contactName;
        }
        if (editTenant.contactEmail != null) {
            postData.contact_email = editTenant.contactEmail;
        }
        if (editTenant.notes != null) {
            postData.notes = editTenant.notes;
        }
        if (editTenant.salesforceId != null) {
            postData.salesforce_id = editTenant.salesforceId;
        }
        if (editTenant.type != null) {
            postData.type = editTenant.type;
        }
        if (editTenant.salesforceLeadId != null) {
            postData.salesforce_lead_id = editTenant.salesforceLeadId;
        }
        if (editTenant.licensingModel != null) {
            postData.licensing_model = editTenant.licensingModel;
        }
        if (editTenant.salesforceContractId != null) {
            postData.salesforce_contract_id = editTenant.salesforceContractId;
        }
        if (editTenant.salesforceContractState != null) {
            postData.salesforce_contract_state = editTenant.salesforceContractState;
        }
        if (editTenant.salesforceContactEmail != null) {
            postData.salesforce_contact_email = editTenant.salesforceContactEmail;
        }
        if (editTenant.salesforceContractEndDate != null) {
            postData.salesforce_contract_end_date = editTenant.salesforceContractEndDate;
        }
        if (editTenant.drawbackPrevention != null) {
            postData.drawback_prevention = editTenant.drawbackPrevention;
        }
        if (
            editTenant.diskQuotaMib != null &&
            editTenant.uploadLimitMib != null &&
            editTenant.downloadLimitMib != null
        ) {
            postData.disk_quota_bytes = editTenant.diskQuotaMib * BYTES_PER_MIB;
            postData.upload_limit_bytes = editTenant.uploadLimitMib * BYTES_PER_MIB;
            postData.download_limit_bytes = editTenant.downloadLimitMib * BYTES_PER_MIB;
        }
        return apiGatewayService.invokeApi(PATH_TENANT + "/" + uuid, "POST", postData, {
            abortSignal: abortController.signal,
            apiType: ApiType.LAUREL,
        });
    }

    public uploadLogo(logo: string, abortController: AbortController): Promise<void> {
        const postData = { image: logo };
        return apiGatewayService.invokeApi("/api/tenant-info/logo", "POST", postData, {
            abortSignal: abortController.signal,
            apiType: ApiType.OLIVER,
            emptyResponse: true,
        });
    }

    public fetchHierarchy(tenantUuid: string, abortController: AbortController): Promise<TenantHierarchyResponse> {
        return apiGatewayService.invokeApi("/api/v1/tenants/" + tenantUuid + "/hierarchy", "GET", null, {
            abortSignal: abortController.signal,
            apiType: ApiType.LAUREL,
        });
    }

    public deleteTenant(tenantUuid: string, region: string, abortController: AbortController): Promise<void> {
        return apiGatewayService.invokeApi("/api/v1/tenant-info/" + tenantUuid, "DELETE", null, {
            abortSignal: abortController.signal,
            apiType: ApiType.OLIVER,
            emptyResponse: true,
            region: region,
        });
    }

    public updateTenantDeletionStatus(tenantUuid: string, abortController: AbortController): Promise<void> {
        return apiGatewayService.invokeApi("/api/v1/tenant-info/" + tenantUuid + "/deletion-status", "POST", null, {
            abortSignal: abortController.signal,
            apiType: ApiType.OLIVER,
            emptyResponse: true,
        });
    }

    public fetchSubTenants(parentUuid: string, abortController: AbortController): Promise<TenantsData> {
        const apiUrl = PATH_TENANT + new ParameterQuery().add("parent_uuid", parentUuid).createQueryString();
        return apiGatewayService
            .invokeApi(apiUrl, "GET", null, { abortSignal: abortController.signal, apiType: ApiType.LAUREL })
            .then((response: TenantsDto) => {
                const tenantList = response.tenants.map(toTenant);
                const cursor = toKeyCursor(response.cursor);
                return {
                    tenantList: {
                        tenantList: tenantList,
                        cursor: cursor,
                    },
                };
            });
    }
}

export const tenantService = new TenantService();
